import { useCallback, useState } from 'react';
import * as R from 'remeda';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { getIDFromUrl, Url } from '@rossum/api-client';
import {
  QueueAction,
  QueueRule,
  SchemaSection,
} from '@rossum/api-client/schemas';
import { ContentTitle } from '@rossum/components/content-group/ContentTitle';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { EmptyState } from '@rossum/components/empty-state/EmptyState';
import { Stack } from '@rossum/ui/material';
import { boldText, button } from '../../../lib/formaterValues';
import { useTableConfig } from '../../document-list-column-config/hooks/useTableConfig';
import {
  RowTypeBase,
  useColumns,
} from '../../document-list-columns/useColumns';
import { useMetaColumns } from '../../document-list-columns/useMetaColumns';
import { FilterPanel } from '../../document-list-filtering/FilterPanel';
import { ValidatedGridFilterItem } from '../../document-list-filtering/types';
import { FormulaPreviewGrid } from '../../formulas/FormulaPreview/FormulaPreviewGrid';
import { UpdateButton } from '../../formulas/FormulaPreview/UpdateButton';
import { FORMULA_PREVIEW_KEY, RULE_PREVIEW_KEY } from '../keys';
import { flattenDatapoints } from './flattenDatapoints';

type FormulaPreviewTileProps = {
  schemaContentDraft: SchemaSection[];
  schemaRulesDraft: Partial<QueueRule>[];
  currentFormulaId: string;
  queueUrls: Url[];
  queue?: { name: string };
  type: 'rule' | 'formula' | 'reasoning' | 'lookup';
  ActionComponent?: React.FC<{ action: QueueAction }> | undefined;
};

const isInvalidSchema = (s: SchemaSection[]) =>
  flattenDatapoints(s).some(schema => !schema.label || !schema.id);

const isInvalidRule = (d: Partial<QueueRule>[]) =>
  d.some(rule => !rule.name || !rule.triggerCondition);

export const FormulaPreviewTile = ({
  schemaContentDraft,
  schemaRulesDraft,
  currentFormulaId,
  queueUrls,
  queue,
  type,
  ActionComponent,
}: FormulaPreviewTileProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const isLookup = type === 'lookup';

  // For lookup fields, we don't auto-calculate preview - user must click the button first
  // We use a function initializer to avoid recreating the initial value on every render
  const [schemaForPreview, setSchemaForPreview] = useState<SchemaSection[]>(
    () => (isLookup ? [] : schemaContentDraft)
  );

  const [ruleForPreview, setRuleForPreview] =
    useState<Partial<QueueRule>[]>(schemaRulesDraft);

  // For lookups, schemaForPreview starts empty until user triggers calculation
  const needsInitialCalculation = isLookup && schemaForPreview.length === 0;

  const isUpToDate =
    R.isDeepEqual(schemaContentDraft, schemaForPreview) &&
    R.isDeepEqual(schemaRulesDraft, ruleForPreview);

  const triggerCalculation = useCallback(() => {
    setSchemaForPreview(schemaContentDraft);
    setRuleForPreview(schemaRulesDraft);

    queryClient.invalidateQueries({
      queryKey: [FORMULA_PREVIEW_KEY],
    });

    queryClient.invalidateQueries({
      queryKey: [RULE_PREVIEW_KEY],
    });
  }, [queryClient, schemaContentDraft, schemaRulesDraft]);

  // This means that the next schema draft is not valid, therefore you need to make adjustments in the form
  const draftInvalid =
    isInvalidSchema(schemaContentDraft) || isInvalidRule(schemaRulesDraft);

  // This means that the schema in state is not valid (usually when creating new field / rule), so you cannot display preview until you update the state
  const previewInvalid =
    isInvalidSchema(schemaForPreview) || isInvalidRule(ruleForPreview);

  const defaultFilters = [
    {
      field: 'status',
      operator: 'isAnyOf',
      value: ['to_review', 'reviewing'],
      isValid: true,
    } as const,
    {
      field: 'queue',
      operator: 'isAnyOf',
      // The chips / filtering component works only for string values
      value: queueUrls.map(getIDFromUrl).map(String),
      isValid: true,
    } as const,
  ];

  const [filters, setFilters] =
    useState<ValidatedGridFilterItem[]>(defaultFilters);

  // This is a bit tricky, we are taking the list of supported schema columns from queue settings
  // However, in the context of field manager, you don't have a selected queue, so we yolo take the first one.
  const firstQueue = queueUrls[0];

  const { columns: tableConfigColumns, isLoading: isTableConfigLoading } =
    useTableConfig({
      orgId: 0,
      queueId: firstQueue ? getIDFromUrl(firstQueue) : 0,
    });

  // TODO: In the filter, maybe we should allow selecting only the queues that the rule is assigned to?
  const { columns: metaColumns } = useMetaColumns<RowTypeBase>({
    annotationColumn: {},
    statusColumn: {},
    detailsColumn: {},
    queueColumn: { level: queue ? 'queue' : 'all' },
    labelsColumn: {},
    documentColumn: {},
  });

  const { columns } = useColumns({
    statusColumns: [], // What do we do about status specific columns? Hide all of them for now?
    metaColumns,
    isTableConfigLoading,
    activeQueueId: firstQueue ? getIDFromUrl(firstQueue) : null, // Queue is used to get list of columns mostly, afaik it does not matter which to take.
    tableColumns: tableConfigColumns,
  });

  return (
    <ContentWrapper>
      <ContentTitle
        title={intl.formatMessage({
          id: `features.formulas.previewType.${type}.title`,
        })}
        description={intl.formatMessage(
          {
            id: `features.formulas.previewType.${type}.description.v2`,
          },
          {
            updateValues: intl.formatMessage({
              id: 'features.queueSettings.fields.formulaPreview.updateValues',
            }),
          }
        )}
      />

      <Stack spacing={2} pt={3}>
        <FilterPanel
          columns={columns}
          validFilters={filters}
          invalidFilters={[]}
          handleFilterModelChange={items =>
            setFilters(items.map(item => ({ ...item, isValid: true })))
          }
          handleClearAllFilters={() => setFilters(defaultFilters)}
          isLoading={false}
          hasActiveQueue={queue !== undefined}
        />

        {previewInvalid ? (
          <EmptyState
            title={intl.formatMessage({
              id: 'features.formulas.formulaFieldSection.emptyTitle',
            })}
            description={intl.formatMessage(
              {
                id: `features.formulas.formulaFieldSection.${draftInvalid ? 'noSchemaId' : 'noFormula'}`,
              },
              draftInvalid
                ? undefined
                : {
                    boldText,
                    button: button(triggerCalculation),
                  }
            )}
          />
        ) : needsInitialCalculation ? (
          <EmptyState
            title={intl.formatMessage({
              id: 'features.queueSettings.fields.formulaPreview.lookups.emptyTitle',
            })}
            description={intl.formatMessage(
              {
                id: 'features.queueSettings.fields.formulaPreview.clickToCalculate',
              },
              {
                boldText,
                button: button(triggerCalculation),
              }
            )}
          />
        ) : (
          <FormulaPreviewGrid
            queueUrls={queueUrls}
            type={type === 'rule' ? 'rule' : 'formula'}
            currentFormulaId={currentFormulaId}
            schemaRules={ruleForPreview}
            schemaForPreview={schemaForPreview}
            ActionComponent={ActionComponent}
            filters={filters}
            queueName={queue?.name}
            pageSizeOptionsOverride={isLookup ? [5] : undefined}
          />
        )}
        <Stack alignItems="end">
          <UpdateButton
            updateValue={triggerCalculation}
            isUpToDate={isUpToDate && !needsInitialCalculation}
            disabled={draftInvalid}
            needsInitialCalculation={needsInitialCalculation}
          />
        </Stack>
      </Stack>
    </ContentWrapper>
  );
};
